// Routes.jsx

// Routes.jsx
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from '../components/auth/Login';
import Registration from '../components/auth/Registration';
import ListingsPage from '../components/listingspage/ListingsPage';
import SingleListingPage from '../components/listingspage/SingleListingPage';
import OnboardingPage from '../components/onboarding/users/OnboardingPage';
import AgentOnboardingPage from '../components/onboarding/company/AgentOnboardingPage';
import PricingPage from '../components/subscriptions/PricingPage';
import AgentDashboard from '../components/dashboard/AgentDashboard';
import UserDashboard from '../components/dashboard/UserDashboard';
import TestimonialsPage from '../components/testimonials/TestimonialsPage';
import UploadPropertyPage from '../components/create-listings/UploadPropertyPage';
import AgentListingsPage from '../components/listingspage/agent/AgentListingsPage';
import EditListingPage from '../components/listingspage/agent/EditListingPage';
import UserQuestionnaireOnboarding from '../components/onboarding/users/UserQuestionnaireOnboarding';
import EditProfile from '../components/profiles/EditProfile';
import CalendarScreen from '../components/schedular/CalendarScreen';
import AdminListingsPage from '../components/admin/AdminListingsPage';
import AboutUs from '../components/about/AboutUs';
import Services from '../components/our-services/Services';
import EmailVerificationPending from '../components/auth/EmailVerificationPending';
import VerifyEmail from '../components/auth/VerifyEmail';
import SmartLocks from '../components/smart-locks/SmartLocks';
import SeamSuccess from '../components/dashboard/SeamSuccess';
import SuccessPage from '../components/Success/SuccessPage';
import Contracts from '../components/contracts/Contracts';
import RentalManagers from '../components/rental-managers/RentalManagersPage';
import PrivacyPolicy from '../components/privacy/PrivacyPolicy';
import TermsAndConditions from '../components/terms/TermsAndConditions';
import MaintenanceVendorAccessPage from '../pages/MaintenanceVendorAccessPage';

// Tenant Portal
import TenantLogin from '../components/tenant-portal/TenantLogin';
import TenantDashboard from '../components/tenant-portal/TenantDashboard';

import RequireAuth from './RequireAuth';
import { useAuth, ROLES } from '../hooks/useAuth';



const AppRoutes = () => {
  const { currentUser } = useAuth(); // ✅ use currentUser from AuthContext

  const toDashboard = (userType) => {
    if (userType === ROLES.AGENT || userType === ROLES.MANAGER) return '/agent-dashboard';
    // If user is a renter (standard user), send to user-dashboard
    if (userType === ROLES.RENTER) return '/user-dashboard';
    // Final fallback for missing status
    return '/user-dashboard';
  };




  return (
    <Routes>
      {/* ✅ Main route: if logged in -> dashboard, else -> login */}
      <Route
        path="/"
        element={
          currentUser ? (
            <Navigate to={toDashboard(currentUser.userType)} replace />
          ) : (
            <Navigate to="/tenant-access/login" replace />
          )
        }
      />

      {/* ✅ Tenant access routes (auto-bounce if already logged in) */}
      <Route
        path="/tenant-access/login"
        element={
          currentUser ? (
            <Navigate to={toDashboard(currentUser.userType)} replace />
          ) : (
            <Login />
          )
        }
      />
      <Route
        path="/tenant-access/register"
        element={
          currentUser ? (
            <Navigate to={toDashboard(currentUser.userType)} replace />
          ) : (
            <Registration />
          )
        }
      />

      {/* ✅ Old routes redirect to new ones */}
      <Route path="/login" element={<Navigate to="/tenant-access/login" replace />} />
      <Route path="/register" element={<Navigate to="/tenant-access/register" replace />} />

      {/* PUBLIC */}
      <Route path="/listings" element={<ListingsPage />} />
      <Route path="/listing/:id" element={<SingleListingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/testimonials" element={<TestimonialsPage />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/services" element={<Services />} />
      <Route path="/email-verification-pending" element={<EmailVerificationPending />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/smart-locks" element={<SmartLocks />} />
      <Route path="/seam-success" element={<SeamSuccess />} />
      <Route path="/success" element={<SuccessPage />} />
      <Route path="/success-payment" element={<SuccessPage />} />
      <Route path="/contracts" element={<Contracts />} />
      <Route path="/rental-managers" element={<RentalManagers />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/maintenance-vendor/:listingId/:linkId?" element={<MaintenanceVendorAccessPage />} />

      {/* TENANT PORTAL */}
      <Route path="/tenant-login" element={<TenantLogin />} />
      <Route path="/tenant-dashboard" element={<TenantDashboard />} />


      {/* ONBOARDING */}
      <Route
        path="/onboarding"
        element={
          <RequireAuth roles={ROLES.RENTER}>
            <OnboardingPage />
          </RequireAuth>
        }
      />
      <Route
        path="/agent-onboarding"
        element={
          <RequireAuth roles={[ROLES.AGENT, ROLES.MANAGER]}>
            <AgentOnboardingPage />
          </RequireAuth>
        }
      />

      {/* PROTECTED */}
      <Route
        path="/agent-dashboard"
        element={
          <RequireAuth roles={[ROLES.AGENT, ROLES.MANAGER]}>
            <AgentDashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/user-dashboard"
        element={
          <RequireAuth roles={ROLES.RENTER}>
            <UserDashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/upload-property"
        element={
          <RequireAuth roles={[ROLES.AGENT, ROLES.MANAGER]}>
            <UploadPropertyPage />
          </RequireAuth>
        }
      />
      <Route
        path="/agent-listings"
        element={
          <RequireAuth roles={[ROLES.AGENT, ROLES.MANAGER]}>
            <AgentListingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/edit-listing/:id"
        element={
          <RequireAuth roles={[ROLES.AGENT, ROLES.MANAGER]}>
            <EditListingPage />
          </RequireAuth>
        }
      />
      <Route
        path="/user-questionnaire-onboarding"
        element={
          <RequireAuth roles={ROLES.RENTER}>
            <UserQuestionnaireOnboarding />
          </RequireAuth>
        }
      />
      <Route
        path="/edit-profile"
        element={
          <RequireAuth>
            <EditProfile />
          </RequireAuth>
        }
      />
      <Route
        path="/calendar"
        element={
          <RequireAuth roles={[ROLES.AGENT, ROLES.MANAGER]}>
            <CalendarScreen />
          </RequireAuth>
        }
      />
      <Route
        path="/admin-manager"
        element={
          <RequireAuth roles={[ROLES.MANAGER]}>
            <AdminListingsPage />
          </RequireAuth>
        }
      />
    </Routes>
  );
};

export default AppRoutes;


